import DatabaseModel from './DatabaseModel.js';
import { unwrapDto } from './responseUtils.js';

var RhVacaciones = Object.create(DatabaseModel);

RhVacaciones.model = 'rh/vacaciones';
RhVacaciones.modelFront = 'mapaaprobacion';

// GET /movilidad/rh/vacaciones/ficha/{username}
// → { username, nombre, rol, gestionaVacaciones, usuarioPrincipal, vinculados[], aprobador,
//     aprobadorSuplente, apruebaA[], diasAnuales, cuspide, estado, avisos[], editable, ... }
// La ven ADMIN y DIRECTOR de cualquiera, y cada cual la suya (editable=false).
RhVacaciones.getFicha = function(username) {
    var self = this;
    return self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/ficha/' + encodeURIComponent(username),
        method: 'GET'
    }, false).then(unwrapDto);
};

// GET /movilidad/rh/vacaciones/empleado/{id} → { id, username, nombre }
// De quién es una ficha de empleado, y nada más. Mismo permiso que la tarjeta.
// [revisión #1711] Existe para no tener que pasar por `GET /empleado/{id}`, que devuelve el
// empleado entero —correo, teléfono, dni, firma— a quien solo tiene permiso sobre las
// vacaciones: no se pintaba, pero se le bajaba al navegador igual.
RhVacaciones.empleado = function(id) {
    const self = this;
    return self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/empleado/' + encodeURIComponent(id),
        method: 'GET'
    }, false).then(unwrapDto);
};

// PUT /movilidad/rh/vacaciones/ficha/{username} → la ficha guardada, ya recalculada.
// Se envía la tarjeta entera: check, «Misma persona que», aprobador, suplente, «aprueba a» y motivo.
RhVacaciones.guardarFicha = function(username, data) {
    var self = this;
    return self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/ficha/' + encodeURIComponent(username),
        method: 'PUT',
        data: data
    }, false).then(unwrapDto);
};

// GET /movilidad/rh/vacaciones/mapa[?incluirInactivos=true] → { filas[], contadores… }
// Llega la plantilla entera de una vez: la pantalla filtra y ordena en memoria.
RhVacaciones.mapa = function(incluirInactivos) {
    var self = this;
    return self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/mapa'
            + (incluirInactivos ? '?incluirInactivos=true' : ''),
        method: 'GET'
    }, false).then(unwrapDto);
};

// POST /movilidad/rh/vacaciones/mapa/asignar-aprobador → { actualizados, sinCambios, errores[] }
// No es todo o nada: lo que no se puede asignar vuelve explicado en errores.
RhVacaciones.asignarAprobador = function(usernames, aprobador, motivo) {
    var self = this;
    return self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/mapa/asignar-aprobador',
        method: 'POST',
        data: { usernames: usernames, aprobador: aprobador, motivo: motivo }
    }, false).then(unwrapDto);
};

// GET /movilidad/rh/vacaciones/bloqueo-generico → { activo }
// ¿Rechaza el servidor las vacaciones (T0006) y los festivos generados (T0083 con grupo FEST-) en
// los caminos genéricos del calendario? (#49). Lo pregunta la barrera de la ruta del calendario
// ANTES de montar la pantalla, para esconder el lápiz, la papelera, el arrastre y «Marcar como
// realizada» de esos eventos EXACTAMENTE cuando el servidor los va a rechazar.
//
// No se puede decidir en el front: con el interruptor apagado —que es como se entrega— las
// vacaciones se siguen cargando a mano por esos mismos caminos, así que esconder los botones
// siempre rompería el trabajo de hoy.
//
// Un fallo NO es «apagado»: rechaza. Devolver false ante cualquier error era lo que convertía
// «todavía no se sabe» en «se puede escribir», y de ahí salían las carreras que el calendario
// venía tapando escritor a escritor (decisión de diseño de 01/10/2026 en la #49). Quien llama
// decide qué hacer con el rechazo; la barrera enseña un error con reintento y no monta el
// calendario. Por el mismo motivo se exige que `activo` sea un booleano de verdad: un cuerpo
// inesperado es un dato que no se sabe leer, no un interruptor apagado.
RhVacaciones.bloqueoGenerico = function() {
    const self = this;
    return self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/bloqueo-generico',
        method: 'GET'
    }, false).then(function(res) {
        const dto = unwrapDto(res);
        if (!dto || typeof dto.activo != 'boolean') {
            throw { message: 'Respuesta inesperada de bloqueo-generico' };
        }
        return dto.activo;
    });
};

// GET /movilidad/rh/vacaciones/bloqueo-generico → { activo, caminoManualApagado }
// #271 · Los dos interruptores que el calendario necesita ANTES de montarse, en la misma consulta:
// el bloqueo genérico de la #49 y el camino manual que acciona Dirección. Lo pide la barrera de la
// ruta en lugar de `bloqueoGenerico`, con la misma regla: un cuerpo que no se sabe leer no es
// «apagado» ni «permitido», rechaza, y la barrera monta con lo restrictivo.
RhVacaciones.interruptoresCalendario = function() {
    const self = this;
    return self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/bloqueo-generico',
        method: 'GET'
    }, false).then(function(res) {
        const dto = unwrapDto(res);
        if (!dto || typeof dto.activo != 'boolean' || typeof dto.caminoManualApagado != 'boolean') {
            throw { message: 'Respuesta inesperada de bloqueo-generico' };
        }
        return { activo: dto.activo, caminoManualApagado: dto.caminoManualApagado };
    });
};

// GET /movilidad/rh/vacaciones/camino-manual → { apagado, coincideConElUltimoApunte, cambios[] }
// #271 · El interruptor del camino manual para la tarjeta de Ajustes · Normas de vacaciones: la
// posición que aplica el servidor y quién lo ha ido accionando. Solo ADMIN y DIRECTOR.
RhVacaciones.caminoManual = function() {
    const self = this;
    return self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/camino-manual',
        method: 'GET'
    }, false).then(unwrapDto);
};

// PUT /movilidad/rh/vacaciones/camino-manual { apagado } → el estado ya cambiado
// #271 · Se manda la posición en la que dejarlo, no «cámbialo»: un doble clic o dos personas a la
// vez piden lo mismo, y el servidor no apunta nada si ya estaba ahí.
RhVacaciones.accionarCaminoManual = function(apagado) {
    const self = this;
    return self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/camino-manual',
        method: 'PUT',
        data: { apagado: apagado }
    }, false).then(unwrapDto);
};

// GET /movilidad/rh/vacaciones/papelera/{idPlanificacion} → { borrable, motivo }
// #268 · ¿Puede la papelera del calendario borrar este día de vacaciones? Las puestas a mano sí,
// sin mirar el rol; las nacidas de una solicitud nunca: se anulan desde Vacaciones (#69). Lo
// contesta el MISMO guardián que aplica el servidor al borrar, así que el botón se dibuja
// exactamente cuando el borrado va a entrar, y `motivo` es lo que se enseña en su lugar.
//
// Como `bloqueoGenerico`, un cuerpo que no se sabe leer no es «se puede»: rechaza, y quien llama
// no dibuja la papelera.
RhVacaciones.papelera = function(idPlanificacion) {
    const self = this;
    return self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/papelera/' + encodeURIComponent(idPlanificacion),
        method: 'GET'
    }, false).then(function(res) {
        const dto = unwrapDto(res);
        if (!dto || typeof dto.borrable != 'boolean') {
            throw { message: 'Respuesta inesperada de papelera' };
        }
        return dto;
    });
};

// GET /movilidad/rh/vacaciones/aprobadores/{username} → quién puede aprobarle (D3, D18).
// Solo lectura: aprobar es F2.
RhVacaciones.aprobadores = function(username) {
    var self = this;
    return self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/aprobadores/' + encodeURIComponent(username),
        method: 'GET'
    }, false).then(unwrapDto);
};

export default RhVacaciones;
